import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AssetAttachment, AssetAttachmentDownloadUrl } from '@dcc-erp/shared';

import { api, API_BASE, HttpError, type ApiError } from '../api';
import { ENTRA_API_SCOPE, msalInstance } from '../auth/msal-config';

// ============================================================
// Asset attachments query hooks. Uploads use multipart/form-data which the
// plain `apiJson` helper can't serialise, so we go through `apiMultipart`
// (defined below) that mirrors the auth + error-handling of `api()` but
// lets `fetch` set the Content-Type/boundary header itself.
// ============================================================

async function authHeaderForMultipart(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return {};
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [ENTRA_API_SCOPE],
    });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch {
    return {};
  }
}

async function apiMultipart<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await authHeaderForMultipart()),
    // Important: do NOT set Content-Type — browser must add the boundary param.
  };
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body,
    headers,
  });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    let errBody: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      errBody = { ...errBody, ...parsed };
    } catch {
      // non-JSON error body
    }
    throw new HttpError(res.status, errBody);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const listKey = (companyId: string | undefined, assetId: string | undefined) =>
  ['asset-attachments', companyId, assetId] as const;

export function useAssetAttachments(companyId: string | undefined, assetId: string | undefined) {
  return useQuery({
    queryKey: listKey(companyId, assetId),
    queryFn: () =>
      api<AssetAttachment[]>(`/companies/${companyId}/fixed-assets/${assetId}/attachments`),
    enabled: !!companyId && !!assetId,
  });
}

export interface UploadAssetAttachmentInput {
  file: File;
  caption?: string;
  kind?: 'IMAGE' | 'DOCUMENT';
}

export function useUploadAssetAttachment(
  companyId: string | undefined,
  assetId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption, kind }: UploadAssetAttachmentInput) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      if (caption) fd.append('caption', caption);
      if (kind) fd.append('kind', kind);
      return apiMultipart<AssetAttachment>(
        `/companies/${companyId}/fixed-assets/${assetId}/attachments`,
        fd,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId, assetId) });
      void qc.invalidateQueries({ queryKey: ['fixed-assets', companyId] });
    },
  });
}

export function useDeleteAssetAttachment(
  companyId: string | undefined,
  assetId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api<AssetAttachment>(
        `/companies/${companyId}/fixed-assets/${assetId}/attachments/${attachmentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId, assetId) });
      void qc.invalidateQueries({ queryKey: ['fixed-assets', companyId] });
    },
  });
}

// ============================================================
// Presigned URL — short-lived (5 min on the server). staleTime of 4 min so
// React Query refreshes just before expiry instead of returning a stale URL.
// ============================================================
export function useAttachmentDownloadUrl(
  companyId: string | undefined,
  assetId: string | undefined,
  attachmentId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['asset-attachment-url', companyId, assetId, attachmentId] as const,
    queryFn: () =>
      api<AssetAttachmentDownloadUrl>(
        `/companies/${companyId}/fixed-assets/${assetId}/attachments/${attachmentId}/url`,
      ),
    enabled: enabled && !!companyId && !!assetId && !!attachmentId,
    staleTime: 4 * 60 * 1000,
  });
}
